import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Store, Copy, Trash2, Share2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { marketplaceAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';
import MiniSparkline from '../components/MiniSparkline';

const Marketplace = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [sort, setSort] = useState('popular');
  const [loading, setLoading] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try { setItems(await marketplaceAPI.list(sort)); } finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, [sort]);  // eslint-disable-line react-hooks/exhaustive-deps

  const copy = async (s) => {
    try {
      await marketplaceAPI.copy(s.id);
      toast({ title: 'Strategy copied', description: 'Open Backtester to run it' });
      const params = new URLSearchParams({
        symbol: 'AAPL', strategy: s.strategy,
      }).toString();
      navigate(`/backtest?${params}`);
    } catch (e) {
      toast({ title: 'Copy failed', description: e?.response?.data?.detail || 'Sign in required', variant: 'destructive' });
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete your published strategy?')) return;
    try { await marketplaceAPI.remove(id); refresh(); } catch (e) {
      toast({ title: 'Delete failed', description: e?.response?.data?.detail || 'Not the author', variant: 'destructive' });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-16">
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
              <Store className="text-purple-600" size={28} /> Strategy Marketplace
            </h1>
            <p className="text-gray-500">Pro users publish strategies, anyone can copy and run them.</p>
          </div>
          <select className="border border-gray-200 rounded-md px-3 py-2 text-sm h-10" value={sort} onChange={(e) => setSort(e.target.value)} data-testid="marketplace-sort">
            <option value="popular">Sort: Popular</option>
            <option value="newest">Sort: Newest</option>
          </select>
        </div>

        {loading && <p className="text-gray-500 text-sm">Loading…</p>}
        {!loading && !items.length && (
          <Card><CardContent className="p-12 text-center text-gray-500" data-testid="marketplace-empty">
            No strategies published yet. Run a backtest and click <b>Publish</b> to be the first.
          </CardContent></Card>
        )}

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((s) => {
            const ret = s.backtest_summary?.total_return_pct;
            const positive = (ret ?? 0) >= 0;
            const sparkColor = positive ? '#16a34a' : '#dc2626';
            return (
            <Card key={s.id} className="hover:shadow-md transition-all flex flex-col" data-testid={`marketplace-item-${s.id}`}>
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between gap-2">
                  <CardTitle className="text-base line-clamp-1">{s.name}</CardTitle>
                  <Badge variant="outline" className="text-xs whitespace-nowrap">{s.copies || 0} copies</Badge>
                </div>
                <p className="text-xs text-gray-500 line-clamp-1">by <b>{s.author_name}</b> · {s.strategy}</p>
              </CardHeader>
              <CardContent className="pt-0 flex-1 flex flex-col">
                {s.backtest_summary?.equity_preview?.length > 1 && (
                  <div className="mb-2 -mx-1">
                    <MiniSparkline data={s.backtest_summary.equity_preview} color={sparkColor} height={36} />
                  </div>
                )}
                <p className="text-xs text-gray-600 mb-2 line-clamp-2">{s.description}</p>
                {s.backtest_summary && (
                  <div className="flex flex-wrap gap-1 mb-3 text-xs">
                    {ret != null && (
                      <Badge variant="outline" className={positive ? 'border-green-500 text-green-700' : 'border-red-500 text-red-700'}>
                        Return {positive ? '+' : ''}{ret}%
                      </Badge>
                    )}
                    {s.backtest_summary.sharpe != null && <Badge variant="outline">Sharpe {s.backtest_summary.sharpe}</Badge>}
                    {s.backtest_summary.max_dd_pct != null && <Badge variant="outline">DD {s.backtest_summary.max_dd_pct}%</Badge>}
                    {s.backtest_summary.win_rate_pct != null && <Badge variant="outline">Win {s.backtest_summary.win_rate_pct}%</Badge>}
                  </div>
                )}
                <div className="flex gap-2 mt-auto">
                  <Button size="sm" className="bg-purple-600 hover:bg-purple-700" onClick={() => copy(s)} data-testid={`marketplace-copy-${s.id}`}><Copy size={12} className="mr-1" /> Copy</Button>
                  <Button size="sm" variant="outline" onClick={() => navigate(`/s/${s.id}`)} data-testid={`marketplace-share-${s.id}`}><Share2 size={12} className="mr-1" /> Share</Button>
                  {user?.id === s.author_id && (
                    <Button size="sm" variant="outline" className="text-red-600" onClick={() => remove(s.id)} data-testid={`marketplace-delete-${s.id}`}><Trash2 size={12} /></Button>
                  )}
                </div>
              </CardContent>
            </Card>
          );
          })}
        </div>
      </div>
    </div>
  );
};

export default Marketplace;
